import { ref, watch, onMounted, onUnmounted } from 'vue'
import { loadFromStorage, saveToStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/keys'

export type ThemeMode = 'system' | 'light' | 'dark'

const themeMode = ref<ThemeMode>(
  loadFromStorage<ThemeMode>(STORAGE_KEYS.THEME_MODE, 'system')
)

const isDark = ref(false)

let mediaQuery: MediaQueryList | null = null

function getSystemPreference(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyTheme() {
  if (themeMode.value === 'system') {
    isDark.value = getSystemPreference()
  } else {
    isDark.value = themeMode.value === 'dark'
  }

  if (isDark.value) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

function handleMediaChange(e: MediaQueryListEvent) {
  if (themeMode.value === 'system') {
    isDark.value = e.matches
    if (e.matches) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }
}

export function useTheme() {
  function setTheme(mode: ThemeMode) {
    themeMode.value = mode
    saveToStorage(STORAGE_KEYS.THEME_MODE, mode)
    applyTheme()
  }

  function toggleTheme() {
    const modes: ThemeMode[] = ['system', 'light', 'dark']
    const currentIndex = modes.indexOf(themeMode.value)
    const nextIndex = (currentIndex + 1) % modes.length
    setTheme(modes[nextIndex])
  }

  onMounted(() => {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', handleMediaChange)
    applyTheme()
  })

  onUnmounted(() => {
    if (mediaQuery) {
      mediaQuery.removeEventListener('change', handleMediaChange)
    }
  })

  watch(themeMode, () => {
    applyTheme()
  })

  return {
    themeMode,
    isDark,
    toggleTheme
  }
}
